import { Controller } from "@hotwired/stimulus";
import escapeAttr from "./admin/massimo/escape_attr";

// Shows the Nexus discount-rule/discount-billings fields on the downsell form only when the selected
// replication group is Nexus, and rebuilds the discount-rule shadcn select's options to match. No AJAX: the
// options for every Nexus replication group on this location group are embedded in the page at render
// time (see Admin::DownsellPlansHelper#nexus_discount_rule_select_data), so this is a pure client-side
// option swap — rules created in Nexus after the page loaded need a refresh to appear.
//
// Attached to the same <form> as retention-discount-switch (a separate controller, not reused here,
// because "is Nexus" is narrower than that controller's "applies retention discount in POS", which also
// covers Sonny's). See that controller's header comment for why both an on-connect and an on-change path
// are needed — single-option replication-group selects render as a hidden input that never fires change.
export default class extends Controller {
  // priceStandardLabel / priceEligibilityLabel: label variants for a Nexus group, where price gates
  // eligibility rather than charging. Both optional — absent on renders with no such field.
  static targets = ["fieldset", "priceStandardLabel", "priceEligibilityLabel"];
  static values = {
    optionsByRgId: Object,
    nexusRgIds: Array,
    initialRgId: String,
    initialRuleId: String,
  };

  connect() {
    // On load, the server-rendered shadcn select has no options (the dashboard field's collection is empty
    // until this controller populates it), so its value is never the plan's real configured rule — restore
    // from initialRuleIdValue (the plan's actual pos_discount_rule_id) instead.
    this.applyReplicationGroup(this.initialRgIdValue, this.initialRuleIdValue);
  }

  onReplicationGroupChange(event) {
    const { name, value } = event.target;
    if (!name || !name.endsWith("[replication_group_id]")) return;
    // The nested scheduled switch has its own group picker; only the downsell's own group matters here.
    if (name.includes("[plan_scheduled_switch_attributes]")) return;

    this.applyReplicationGroup(value, this.currentRuleId());
  }

  applyReplicationGroup(rgId, previousValue) {
    const isNexus = this.nexusRgIdsValue.map(String).includes(String(rgId));

    this.fieldsetTargets.forEach((fieldset) => {
      fieldset.disabled = !isNexus;
      // Inline style, not a "hidden" class — the fieldset already carries Tailwind's `contents` class
      // (display: contents), and a second display-affecting class would race it on specificity.
      fieldset.style.display = isNexus ? "" : "none";
    });

    this.priceStandardLabelTargets.forEach((el) => (el.hidden = isNexus));
    this.priceEligibilityLabelTargets.forEach((el) => (el.hidden = !isNexus));

    const options = isNexus ? this.optionsByRgIdValue[rgId] || [] : [];
    // The previous group's rule id is never valid for the newly selected group, except when re-applying the
    // same group's options unchanged (e.g. on initial connect) — only keep it if it's still present.
    const keep = options.some(([, value]) => value === previousValue) ? String(previousValue || "") : "";
    this.populateRules(options, keep);
  }

  // Writes the value to the hidden input/dataset directly (fires no change event) since the select
  // controller may not have connected yet on initial load; its display is refreshed here if it has.
  populateRules(options, selectedValue) {
    const wrapper = this.ruleSelectWrapper();
    if (!wrapper) return;

    const group = wrapper.querySelector('[role="group"]');
    if (!group) return;
    group.innerHTML = options.map(([label, value]) => this.optionMarkup(String(value), String(label), selectedValue)).join("");

    const hidden = this.ruleHiddenInput();
    if (hidden) hidden.value = selectedValue;
    wrapper.dataset.selectSelectedValue = selectedValue;

    const select = this.application.getControllerForElementAndIdentifier(wrapper, "select");
    if (select) {
      select.selectedValue = selectedValue;
      select.updateCheckmarks();
      select.updateDisplay();
      select.updateHiddenInput();
    }
  }

  // Mirrors Shadcn::SelectComponent#render_select_option.
  optionMarkup(value, label, selectedValue) {
    const v = escapeAttr(value);
    const l = escapeAttr(label);
    const check =
      value === String(selectedValue)
        ? '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">' +
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>'
        : "";
    return (
      '<div data-ui--filter-target="item">' +
      '<div class="flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer ' +
      'hover:bg-accent hover:text-accent-foreground" data-select-target="option" ' +
      `data-select-value="${v}" data-action="click->select#selectOption">` +
      `<div class="w-4 h-4 shrink-0 text-brand-500">${check}</div>` +
      `<span class="flex-1 flex items-center gap-2">${l}</span>` +
      "</div></div>"
    );
  }

  ruleSelectWrapper() {
    const hidden = this.ruleHiddenInput();
    return hidden ? hidden.closest('[data-controller~="select"]') : null;
  }

  ruleHiddenInput() {
    return this.element.querySelector('input[data-select-target="hiddenInput"][name$="[pos_discount_rule_id]"]');
  }

  currentRuleId() {
    return this.ruleHiddenInput()?.value ?? "";
  }
}
